import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

// Context can be at Organization or Division level
export interface Context {
  id: string;
  name: string;
  organizationId: string | null;
  divisionId: string | null;
  /** The division's own name, absent for an organization-level context. What
   *  groups and orders the list — see `compareContexts`. */
  divisionName?: string | null;
  /** The organization's home context: where its owner's access lives. The API
   *  refuses to rename or delete it, so never offer those controls for it. */
  isOrgHome?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface ContextsListResponse {
  contexts: Context[];
}

const backendApi = config.backendApi;

/**
 * Reading order for a context list: the organization first, then its divisions
 * grouped by division name.
 *
 * The organization context is the broader view and the one most people work in, so
 * it leads. Everything below it is keyed on the **division's** name rather than the
 * context's, which is what actually puts one division's contexts together: MSP
 * onboarding mints `<Customer> Admin`, so the two agree at first, and stop agreeing
 * the moment somebody renames either half. Contexts inside a division then sort by
 * their own name.
 *
 * The id is a tiebreaker rather than decoration. Neither context endpoint has an
 * `ORDER BY`, so two rows that compare equal — two organizations each with an "Org
 * Admin" — can come back in either order on any poll, and an unstable order means
 * a new array identity every time, which defeats React Query's structural sharing
 * and rebuilds every consumer. A total order makes the result depend on the rows
 * and not on the sequence they arrived in.
 *
 * A division whose name is missing (an older payload, or a division soft-deleted
 * out from under the context) falls back to its id, so it still sorts somewhere
 * stable instead of drifting.
 */
export function compareContexts(a: Context, b: Context): number {
  const aIsDivision = a.divisionId ? 1 : 0;
  const bIsDivision = b.divisionId ? 1 : 0;
  if (aIsDivision !== bIsDivision) return aIsDivision - bIsDivision;

  const aDivision = a.divisionName ?? a.divisionId ?? '';
  const bDivision = b.divisionName ?? b.divisionId ?? '';
  return aDivision.localeCompare(bDivision) || a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
}

interface OrgGroup {
  orgId: string;
  orgName: string;
  contexts: Context[];
}

/**
 * Bucket the available contexts by their owning organization. Users in a single
 * org (the common case) get one unlabeled group — no clutter; users spanning
 * multiple orgs get a labeled group per org so it's clear at a glance which org
 * each context belongs to.
 *
 * Groups come out ordered by organization name, and the contexts inside each one
 * keep the order `useContexts` sorted them into (the organization context, then
 * its divisions by name). Neither ordering can be left to the API: no context
 * endpoint has an `ORDER BY`, so the menu was listing "Widgets Inc Admin, Org
 * Admin, Office Corp Admin" — whatever sequence the rows happened to arrive in,
 * re-shuffled on any poll.
 */
export function groupByOrg(contexts: Context[], orgNameById: Map<string, string>): OrgGroup[] {
  const byOrg = new Map<string, OrgGroup>();
  for (const c of contexts) {
    const orgId = c.organizationId ?? '—';
    const existing = byOrg.get(orgId);
    if (existing) {
      existing.contexts.push(c);
    } else {
      byOrg.set(orgId, {
        orgId,
        orgName: orgNameById.get(orgId) ?? 'Organization',
        contexts: [c],
      });
    }
  }
  // Sorted by name, with the id as a tiebreaker so two organizations sharing a
  // name cannot swap places between renders.
  return Array.from(byOrg.values()).sort((a, b) => a.orgName.localeCompare(b.orgName) || a.orgId.localeCompare(b.orgId));
}

const fetchContexts = async (): Promise<Context[]> => {
  const response = await axios.get<ContextsListResponse>(`${backendApi}/api/contexts`, { withCredentials: true });
  return [...response.data.contexts].sort(compareContexts);
};

const fetchAvailableContexts = async (): Promise<Context[]> => {
  const response = await axios.get<ContextsListResponse>(`${backendApi}/api/contexts/available`, { withCredentials: true });
  return [...response.data.contexts].sort(compareContexts);
};

/**
 * Every context in the organization you may read — the administrative view.
 *
 * For "which context may I work in", use `useAvailableContexts`: this one
 * includes contexts you hold no role in, which is right when managing them and
 * wrong when choosing one to act in.
 */
export function useContexts(organizationId: string | null | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<Context[]>({
    queryKey: ['contexts', organizationId],
    queryFn: () => {
      if (!organizationId) {
        throw new Error("organizationId is required to fetch contexts.");
      }
      return fetchContexts();
    },
    enabled: !!organizationId && contextReady,
  });
}

// Hook for getting available contexts for context switching
// This doesn't require permissions - it shows all contexts the user has team membership in
/**
 * Every context you can act in, reached through a team role — **across all
 * organizations you belong to**. What the context switcher needs, since
 * crossing organizations is the whole point of it.
 *
 * `cacheKeyOrganizationId` does NOT filter the result, and is named for what it
 * does: key the cache and hold the query until the caller's organization is
 * known. `GET /api/contexts/available` takes no organization — it answers from
 * team membership alone (`contexts.rs::list_available`). The old name for this
 * parameter was `organizationId`, which read like a filter and was twice built
 * on as though it were, putting other organizations' contexts into pickers that
 * can only submit one organization's.
 *
 * Choosing a context to *act in* almost always means one organization: use
 * [`useAvailableContextsInOrg`]. Reach for this one only when you mean to span
 * them, and then label each group with its organization ([`groupByOrg`]) —
 * "Org Admin" is not a distinguishing name across two organizations.
 */
export function useAvailableContexts(cacheKeyOrganizationId: string | null | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<Context[]>({
    queryKey: ['availableContexts', cacheKeyOrganizationId],
    queryFn: () => {
      if (!cacheKeyOrganizationId) {
        throw new Error("organizationId is required to fetch available contexts.");
      }
      return fetchAvailableContexts();
    },
    enabled: !!cacheKeyOrganizationId && contextReady,
  });
}

/**
 * The contexts you can act in that belong to one organization. The one to feed
 * a picker whose result is submitted against that organization.
 *
 * Pass the organization the *work* belongs to, not the viewer's — for a
 * remediation that is the incident's organization, which is what the API
 * validates the chosen context against (`change_managements.rs`: "Context
 * belongs to a different organization"). Offering the rest is offering a
 * guaranteed 400.
 *
 * Filtering happens in `select`, so this shares one cache entry and one request
 * with [`useAvailableContexts`] rather than refetching the same list.
 */
export function useAvailableContextsInOrg(organizationId: string | null | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<Context[], Error, Context[]>({
    queryKey: ['availableContexts', organizationId],
    queryFn: () => {
      if (!organizationId) {
        throw new Error("organizationId is required to fetch available contexts.");
      }
      return fetchAvailableContexts();
    },
    enabled: !!organizationId && contextReady,
    select: (contexts) => contexts.filter((c) => c.organizationId === organizationId),
  });
}

/**
 * Where a context picker should start: the context the user is already working
 * in when it is one of the choices, otherwise the first.
 *
 * "Otherwise the first" is not arbitrary — `compareContexts` puts the
 * organization-level context ahead of any division, so the fallback is the
 * broadest context on offer rather than whichever row the API happened to
 * return first.
 *
 * Returns undefined for an empty or not-yet-loaded list, so callers can leave
 * the field on its placeholder until there is something to choose.
 */
export function preferredContext(
  contexts: Context[] | undefined,
  activeContextId: string | null | undefined,
): Context | undefined {
  if (!contexts?.length) return undefined;
  return contexts.find((c) => c.id === activeContextId) ?? contexts[0];
}
